// src/composables/usePwaInstall.js
//
// PRD-022 INC-1 RF-3 — convite de instalação do PWA (Android + iOS).
//
// Estado singleton no nível do módulo: o evento `beforeinstallprompt` dispara
// cedo no ciclo de vida da página e UMA única vez. Se cada caller registrasse
// o listener no mount, o evento já teria passado e o convite Android nunca
// apareceria. Por isso capturamos no módulo e compartilhamos via refs.
//
// API:
//   const { canPrompt, isStandalone, isIos, isSafari, isDeferred,
//           shouldShowInvite, promptInstall, deferInvite } = usePwaInstall();
//
//   - canPrompt        : Android capturou o beforeinstallprompt (pode chamar prompt())
//   - isStandalone     : app já rodando instalado (display-mode standalone ou navigator.standalone)
//   - isIos / isSafari  : caminho do guia manual (iOS não expõe beforeinstallprompt)
//   - isDeferred       : usuário adiou o convite (persistido em localStorage, TTL 7 dias)
//   - shouldShowInvite : computed que decide se o componente de convite renderiza
//   - promptInstall()  : apresenta o prompt nativo (Android); retorna 'accepted'|'dismissed'|null
//   - deferInvite()    : adia o convite por DEFER_TTL_MS

import { ref, computed, readonly } from "vue";
import { logger } from "@/utils/logger";

const DEFER_KEY = "pwa_install_deferred_until";
const DISMISS_KEY = "pwa_install_dismissed"; // "Não" — dispensa permanente do convite
const DEFER_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

// Estado compartilhado entre todos os callers.
const deferredPrompt = ref(null);
const canPrompt = ref(false);
let initialized = false;

function detectStandalone() {
  const byDisplayMode =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(display-mode: standalone)").matches
      : false;
  const byNavigator =
    typeof navigator !== "undefined" && navigator.standalone === true;
  return byDisplayMode || byNavigator;
}

function detectIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ se identifica como "Macintosh" — distingue pelo touch.
  const isIPadOS =
    /Macintosh/.test(ua) &&
    typeof document !== "undefined" &&
    "ontouchend" in document;
  return isIDevice || isIPadOS;
}

function detectSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome|Android/.test(ua);
}

// Só convidamos a instalar em APARELHO MÓVEL — no desktop o PWA instalado tem
// usabilidade ruim, então nunca pedimos lá (mesmo quando o Chrome oferece).
function detectMobile() {
  if (typeof navigator === "undefined") return false;
  // Sinal mais confiável no Chromium (Android Chrome): userAgentData.mobile.
  if (
    navigator.userAgentData &&
    typeof navigator.userAgentData.mobile === "boolean"
  ) {
    return navigator.userAgentData.mobile;
  }
  const ua = navigator.userAgent || "";
  return (
    /Android|iPhone|iPad|iPod|Windows Phone|webOS|BlackBerry|Mobile/i.test(ua) ||
    detectIos()
  );
}

function setupListeners() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (e) => {
    // Impede a mini-infobar nativa — controlamos o convite na UI do app.
    e.preventDefault();
    deferredPrompt.value = e;
    canPrompt.value = true;
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt.value = null;
    canPrompt.value = false;
    try {
      localStorage.removeItem(DEFER_KEY);
    } catch (err) {
      logger.error("[pwa] Falha ao limpar estado pós-instalação:", err);
    }
  });
}

function readDeferred() {
  try {
    const raw = localStorage.getItem(DEFER_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (Number.isNaN(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
}

function readDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === "true";
  } catch {
    return false;
  }
}

export function usePwaInstall() {
  setupListeners();

  const isStandalone = computed(() => detectStandalone());
  const isIos = detectIos();
  const isSafari = detectSafari();
  const isMobile = detectMobile();
  const isDeferred = ref(readDeferred());
  const isDismissed = ref(readDismissed());

  // Há caminho de instalação ACIONÁVEL? (botão em Configurações) — em mobile, não
  // instalado, e com Android prompt capturado OU iOS Safari (guia). Ignora
  // deferred/dismissed: é uma ação explícita do usuário, sempre disponível.
  const canInstall = computed(
    () =>
      isMobile &&
      !isStandalone.value &&
      (canPrompt.value || (isIos && isSafari)),
  );

  function deferInvite() {
    try {
      localStorage.setItem(DEFER_KEY, String(Date.now() + DEFER_TTL_MS));
    } catch (err) {
      logger.error("[pwa] Falha ao adiar convite de instalação:", err);
    }
    isDeferred.value = true;
  }

  // "Não" — dispensa o convite automático de vez (o botão em Configurações segue
  // disponível para quem mudar de ideia).
  function dismissInvite() {
    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch (err) {
      logger.error("[pwa] Falha ao dispensar convite de instalação:", err);
    }
    isDismissed.value = true;
  }

  async function promptInstall() {
    const evt = deferredPrompt.value;
    if (!evt) return null;

    evt.prompt();
    let outcome = null;
    try {
      const choice = await evt.userChoice;
      outcome = choice?.outcome ?? null;
    } catch (err) {
      logger.error("[pwa] Erro ao apresentar prompt de instalação:", err);
    }
    deferredPrompt.value = null;
    canPrompt.value = false;
    return outcome;
  }

  const shouldShowInvite = computed(() => {
    if (!isMobile) return false; // só convidamos em mobile (desktop não)
    if (isStandalone.value) return false; // CA-4: já instalado
    if (isDismissed.value) return false; // "Não" — dispensado de vez
    if (isDeferred.value) return false; // CA-4: adiado
    if (canPrompt.value) return true; // CA-1: Android
    if (isIos && isSafari) return true; // CA-2: iOS guia manual
    return false;
  });

  return {
    canPrompt: readonly(canPrompt),
    isStandalone,
    isIos,
    isSafari,
    isMobile,
    canInstall,
    isDeferred: readonly(isDeferred),
    isDismissed: readonly(isDismissed),
    shouldShowInvite,
    promptInstall,
    deferInvite,
    dismissInvite,
  };
}
